import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import ContentHolder from 'components/layouts/ContentHolder'
import TitleHolder from 'components/layouts/TitleHolder'
import { Col, Container, Row } from 'reactstrap'
import Icon from 'components/layouts/Icon'
import { useHistory } from 'react-router-dom'
import { useDocumentTitle } from '../../components/hooks/useDocumentTitle'
import Cookies from 'js-cookie'
import useApiCall from '../../components/api/useApiCall'
import bottom from '../../assets/media/migrate/bottom-polygon.png'
import circle from '../../assets/media/migrate/ellipse.png'
import top from '../../assets/media/migrate/top-polygon.png'
import { getAccountRedirectionUrl } from '../../utils/utils'
import { trackPageView, trackObjectClicked } from '../../configs/mixpanel'

const SUPPORT_URL = 'https://integosupport.zendesk.com/hc'

const ELIGIBLE_SKU_REGEX = /^BN_(IS|MPB)_X9/

const Main = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const [lang, setLang] = useState(window.localStorage.getItem('i18nextLng'))
  const [{ isLoaded, data, error }] = useApiCall('subscriptions')

  useDocumentTitle(t('welcome.page_title'))

  useEffect(() => {
    trackPageView('my_account_welcome')
  }, [])

  useEffect(() => {
    setLang(window.localStorage.getItem('i18nextLng'))
  }, [lang])

  const subscriptions = Array.isArray(data?.message?.subscriptions)
    ? data.message.subscriptions
    : null
  const subscriptionsReady = isLoaded && subscriptions !== null

  const hasOneSub = subscriptions?.some((s) => s.sku?.startsWith('ONE_'))

  if (hasOneSub) {
    const host = window.location.host;
    let email, token;
    try {
      email = localStorage.getItem('e');
      token = localStorage.getItem('h');
    } catch (err) {
      console.error('localStorage unavailable:', err);
    }
    try {
      window.location.href = getAccountRedirectionUrl(host, email, token);
    } catch (err) {
      console.error('Redirection failed:', err);
    }
  }

  const hasEligibleSku = subscriptions?.some((s) => ELIGIBLE_SKU_REGEX.test(s.sku))

  const hasEligibleX9Sub = subscriptions?.some(
    (s) => s.product?.includes('X9') && !(s.winSeats > 0) && !s.isCanceled && s.isActive
  )

  const hasRenewalSku = subscriptions?.some((s) => s.sku?.includes('_R_'))
  
  const showIntegoOne = subscriptionsReady && !hasOneSub && hasEligibleSku && hasEligibleX9Sub && !hasRenewalSku

  const baseCards = [
    { id: 1, title: t('account.title'),   paragraph: t('account.paragraph'),   icon: 'account',   link: 'account',   className: 'account-card'   },
    { id: 2, title: t('devices.title'),   paragraph: t('devices.paragraph'),   icon: 'devices',   link: 'devices',   className: 'devices-card'   },
    { id: 3, title: t('invoices.title'),  paragraph: t('invoices.paragraph'),  icon: 'invoices',  link: 'invoices',  className: 'invoices-card'  },
    { id: 4, title: t('support.title'),   paragraph: t('support.paragraph'),   icon: 'support',   link: 'support',   className: 'support-card'   },
    { id: 5, title: t('knowledge.title'), paragraph: t('knowledge.paragraph'), icon: 'knowledge', link: 'knowledge', className: 'knowledge-card' },
  ]

  const integoOneCard = {
    id: 6,
    title:     t('intego_one.title'),
    paragraph: t('intego_one.paragraph'),
    icon:      'intego-one',
    link:      'intego-one',
    className: 'intego-one-card',
  }

  const cards = showIntegoOne ? [...baseCards, integoOneCard] : baseCards

  const handleCardClick = ({ id, link }) => {
    const safeLang = document.documentElement.lang || 'en'
    if (id === 4) return window.open(`${SUPPORT_URL}/${safeLang}/requests/new`, '_blank', 'noopener')
    if (id === 5) return window.open(`${SUPPORT_URL}/${safeLang}`, '_blank', 'noopener')
    if (id === 6) {
      trackObjectClicked('get_intego_one')
      try {
        Cookies.set('h', localStorage.getItem('e') || '')
      } catch (err) {
        console.error('localStorage unavailable:', err);
      }
      return history.push('/migrate', { fromApp: true })
    }
    history.push(link, 'cards')
  }

  if (error) {
    return (
      <div className="page--main">
        <ContentHolder>
          <TitleHolder text={t('welcome.title')} />
          <p>{t('error.load_failed')}</p>
        </ContentHolder>
      </div>
    )
  }

  return (
    <div className="page--main">
      <ContentHolder>
        <TitleHolder text={t('welcome.title')} />
        <Container className="p-0">
          <Row>
            {!subscriptionsReady
              ? baseCards.map((card) => (
                  <Col key={card.id} lg={4} md={6} sm={12} className="card-wrapper">
                    <div className="card-nav card-skeleton">
                      <div className="skeleton skeleton-icon" />
                      <div className="content">
                        <div className="skeleton skeleton-title" />
                        <div className="skeleton skeleton-text" />
                        <div className="skeleton skeleton-text short" />
                      </div>
                    </div>
                  </Col>
                ))
              : cards.map((card) => (
              <Col key={card.id} lg={4} md={6} sm={12} className="card-wrapper">
                <div onClick={() => handleCardClick(card)}>
                  {card.id === 6 ? (
                    <div className="card-nav intego-one-card">
                      <img src={top} alt="" className="deco-top-img" />
                      <img src={circle} alt="" className="deco-circle-img" />
                      <img src={bottom} alt="" className="deco-bottom-img" />
                      <div className="icon"><Icon icon={card.icon} /></div>
                      <div className="content">
                        <div className="title-row">
                          <h3 className="title">{card.title}</h3>
                          <div className="new-pill">{t('NEW')}</div>
                        </div>
                        <p>{card.paragraph}</p>
                      </div>
                    </div>
                  ) : (
                    <div className={`card-nav ${card.className}`}>
                      <div className="icon"><Icon icon={card.icon} /></div>
                      <div className="content">
                        <h3 className="title">{card.title}</h3>
                        <p>{card.paragraph}</p>
                      </div>
                    </div>
                  )}
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </ContentHolder>
    </div>
  )
}

export default Main